import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, lastValueFrom, Observable, throwError} from "rxjs";
import {LoggedUserReport, User} from "../../shared/models/user";
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {ErrorHandlerService} from "./error-handler.service";
import {OidcSecurityService} from "angular-auth-oidc-client";
import {UserService} from "./user.service";
import {CompanyService} from "./company.service";
import {getReportIcon, IUserAccountsQueryParams, objectPropertiesToCamelCase} from "../../shared";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  loggedUser$: BehaviorSubject<User | undefined> = new BehaviorSubject<User | undefined>(undefined);
  // Estado global para credenciales de Odoo
  isOdooCredentialsValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isMojitoCredentialsValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly baseUrl = environment.baseBackendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private oidcSecurityService: OidcSecurityService,
    private userService: UserService,
  ) {
  }

  get loggedUser(): Observable<User | undefined> {
    return this.loggedUser$ as Observable<User | undefined>;
  }

  set loggedUser(value: User | undefined) {
    this.loggedUser$.next(value);
  }

  async loadAllUserData(): Promise<void> {
    const userParams: IUserAccountsQueryParams = {
      $select: 'DisplayName,Email,Id,LastLoginAt',
      $expand: 'Roles($expand=Permissions),Companies,Reports($expand=DataSourceProviders),FavoriteReports($expand=DataSourceProviders)',
    }
    const userId = (this.loggedUser$.value!.userId || this.loggedUser$.value!.id)!;
    try {
      const userRsp = await lastValueFrom(this.userService.getUserAccount(userId, userParams));
      // Configurar los iconos segun la key del reporte
      userRsp.reports = userRsp.reports.map((r: any) => ({...r, icon: getReportIcon(r.key)}));
      this.loggedUser = objectPropertiesToCamelCase(userRsp);
    } catch (e) {
      console.error(e);
      this.oidcSecurityService.logoff().subscribe(() => {});
    }
  }

  /**
   * Actualiza el estado global de validez de credenciales Odoo
   */
  setOdooCredentialsValid(valid: boolean): void {
    this.isOdooCredentialsValid$.next(!!valid);
  }

  /**
   * Actualiza el estado de favorito de un reporte en el usuario actual
   */
  toggleFavoriteReport(reportId: number, isFavorite: boolean): void {
    const currentUser = this.loggedUser$.value;
    if (!currentUser?.reports) return;

    const targetReport = currentUser.reports.find(report => report.id === reportId);
    if (!targetReport) return;

    const currentFavorites = currentUser.favoriteReports || [];
    let favoriteReportsToUpdate: LoggedUserReport[] = [];

    if (isFavorite) {
      // Evitar duplicados y crear nuevo array
      favoriteReportsToUpdate = currentFavorites.some(r => r.id === reportId)
        ? currentFavorites
        : [...currentFavorites, targetReport];
    } else {
      favoriteReportsToUpdate = currentFavorites.filter(r => r.id !== targetReport.id)
    }

    this.loggedUser = {...currentUser, favoriteReports: favoriteReportsToUpdate};
  }

  /**
   * Obtiene el usuario logueado
   */
  getLoggedUser(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/Auth/me`).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  postAdminInitialize(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/Admin/initialize`, {}).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  logout() {
    this.loggedUser = undefined;
    this.oidcSecurityService
      .logoff()
      .subscribe((result) => {});
  }

}
